'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

interface OrgProfile {
  id: string;
  name: string;
  plan: 'starter' | 'growth' | 'pro';
  currentPlan: string;
  maxWarehouses: number;
  maxSkus: number;
  maxIntegrations: number;
  maxOrdersPerMonth: number;
  subscriptionStatus: string;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  isDemo: boolean;
}

interface UserProfile {
  id: string;
  orgId: string;
  email: string;
  fullName: string;
  role: 'org_owner' | 'admin' | 'operator';
  avatarUrl?: string;
  isActive: boolean;
}

// Client portal user profile — for external customers
export interface ClientPortalUser {
  id: string; // client_portal_users.id
  userId: string; // auth user id
  clientPortalId: string;
  clientPortalName: string;
  parentOrgId: string;
  email: string;
  fullName: string;
  inviteStatus: 'pending' | 'accepted' | 'disabled';
}

interface AuthContextType {
  user: any;
  session: any;
  userProfile: UserProfile | null;
  orgProfile: OrgProfile | null;
  clientPortalUser: ClientPortalUser | null;
  isClientPortalUser: boolean;
  loading: boolean;
  /** True when a stale refresh token was rejected — show "Session expired" UI */
  sessionExpired: boolean;
  /** True when profile/org records are missing after sign-in — show recovery UI */
  provisioningError: boolean;
  /** Call this after the user acknowledges the expired-session message */
  clearSessionExpired: () => void;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isAdmin: () => boolean;
  isOwner: () => boolean;
  canManageUsers: () => boolean;
  canCreatePOs: () => boolean;
  isTrialExpired: () => boolean;
  isReadOnly: () => boolean;
  trialDaysRemaining: () => number;
  isDemoOrg: () => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [orgProfile, setOrgProfile] = useState<OrgProfile | null>(null);
  const [clientPortalUser, setClientPortalUser] = useState<ClientPortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [provisioningError, setProvisioningError] = useState(false);
  const router = useRouter();

  // Stable supabase client — created once, never recreated on re-render
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  // Prevent duplicate profile fetches for the same user
  const profileFetchedForRef = useRef<string | null>(null);
  // Prevent Strict Mode double-subscription
  const listenerMountedRef = useRef(false);
  // Prevent duplicate refresh-failure handling
  const refreshFailureHandledRef = useRef(false);

  const clearAuthState = () => {
    setSession(null);
    setUser(null);
    setUserProfile(null);
    setOrgProfile(null);
    setClientPortalUser(null);
    setProvisioningError(false);
    profileFetchedForRef.current = null;
    setLoading(false);
  };

  const clearSessionExpired = () => setSessionExpired(false);

  /**
   * Called when a refresh token is rejected (400 or 429) or TOKEN_REFRESHED fires
   * with no session.
   */
  const handleRefreshFailure = async () => {
    if (refreshFailureHandledRef.current) return;
    refreshFailureHandledRef.current = true;

    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // Ignore — token is already invalid
    }
    clearAuthState();
    setSessionExpired(true);
    router.replace('/login?expired=1');
  };

  /**
   * Fetch org profile from user_profiles + organizations.
   * Retries up to 3 times with increasing delays to handle the race condition
   * where the DB trigger hasn't finished creating records by the time the
   * session lands on the client.
   * If still missing after all retries, sets provisioningError=true so the
   * dashboard can show a clear recovery state instead of an infinite skeleton.
   */
  const fetchProfiles = async (userId: string, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1500, 2500, 4000]; // ms between retries

    if (profileFetchedForRef.current === userId && retryCount === 0) return;
    if (retryCount === 0) profileFetchedForRef.current = userId;

    console.log(
      `[AuthContext] fetchProfiles userId=${userId} attempt=${retryCount + 1}/${MAX_RETRIES + 1}`
    );

    try {
      // Regular org user — fetch user_profile + org
      const { data: profile, error: profileErr } = await supabase
        .from('user_profiles')
        .select('*, organizations(*)')
        .eq('id', userId)
        .maybeSingle();

      if (profileErr) {
        console.error(`[AuthContext] fetchProfiles error for userId=${userId}:`, profileErr.message);
      }

      if (profile) {
        console.log(
          `[AuthContext] ✅ user_profile found for userId=${userId} org_id=${profile.org_id}`
        );
        setProvisioningError(false);
        setUserProfile({
          id: profile.id,
          orgId: profile.org_id,
          email: profile.email,
          fullName: profile.full_name,
          role: profile.role,
          avatarUrl: profile.avatar_url,
          isActive: profile.is_active,
        });

        if (profile.organizations) {
          const org = profile.organizations;
          console.log(
            `[AuthContext] ✅ org found: id=${org.id} plan="${org.plan}" current_plan="${org.current_plan}"`
          );
          setOrgProfile({
            id: org.id,
            name: org.name,
            plan: org.plan,
            currentPlan: org.current_plan || org.plan,
            maxWarehouses: org.max_warehouses,
            maxSkus: org.max_skus,
            maxIntegrations: org.max_integrations ?? 3,
            maxOrdersPerMonth: org.max_orders_per_month ?? 500,
            subscriptionStatus: org.subscription_status || 'trialing',
            trialStartedAt: org.trial_started_at || null,
            trialEndsAt: org.trial_ends_at || null,
            stripeCustomerId: org.stripe_customer_id || null,
            stripeSubscriptionId: org.stripe_subscription_id || null,
            isDemo: org.is_demo ?? false,
          });
        } else {
          // Profile exists but org join returned null — fetch org directly
          console.warn(
            `[AuthContext] ⚠ user_profile found but organizations join is null for userId=${userId}, org_id=${profile.org_id} — fetching org directly`
          );
          if (profile.org_id) {
            const { data: orgDirect, error: orgDirectErr } = await supabase
              .from('organizations')
              .select('*')
              .eq('id', profile.org_id)
              .maybeSingle();

            if (orgDirectErr) {
              console.error(`[AuthContext] ✖ Direct org fetch error for org_id=${profile.org_id}:`, orgDirectErr.message);
            }

            if (orgDirect) {
              console.log(
                `[AuthContext] ✅ org fetched directly: id=${orgDirect.id} plan="${orgDirect.plan}" current_plan="${orgDirect.current_plan}"`
              );
              setOrgProfile({
                id: orgDirect.id,
                name: orgDirect.name,
                plan: orgDirect.plan,
                currentPlan: orgDirect.current_plan || orgDirect.plan,
                maxWarehouses: orgDirect.max_warehouses,
                maxSkus: orgDirect.max_skus,
                maxIntegrations: orgDirect.max_integrations ?? 3,
                maxOrdersPerMonth: orgDirect.max_orders_per_month ?? 500,
                subscriptionStatus: orgDirect.subscription_status || 'trialing',
                trialStartedAt: orgDirect.trial_started_at || null,
                trialEndsAt: orgDirect.trial_ends_at || null,
                stripeCustomerId: orgDirect.stripe_customer_id || null,
                stripeSubscriptionId: orgDirect.stripe_subscription_id || null,
                isDemo: orgDirect.is_demo ?? false,
              });
            } else {
              console.error(
                `[AuthContext] ✖ MISSING org record for org_id=${profile.org_id} — org does not exist in DB`
              );
            }
          }
        }
        setClientPortalUser(null);
        return;
      }

      // No user_profile found — check if this is a client portal user
      console.log(`[AuthContext] No user_profile for userId=${userId} — checking client_portal_users`);
      const { data: cpuData } = await supabase
        .from('client_portal_users')
        .select('id, client_portal_id, email, full_name, invite_status, client_portals(id, name, parent_org_id)')
        .eq('user_id', userId)
        .eq('invite_status', 'accepted')
        .maybeSingle();

      if (cpuData) {
        const portal = cpuData.client_portals as any;
        console.log(`[AuthContext] ✅ client_portal_user found for userId=${userId}`);
        setClientPortalUser({
          id: cpuData.id,
          userId,
          clientPortalId: cpuData.client_portal_id,
          clientPortalName: portal?.name || '',
          parentOrgId: portal?.parent_org_id || '',
          email: cpuData.email,
          fullName: cpuData.full_name,
          inviteStatus: cpuData.invite_status,
        });
        setUserProfile(null);
        setOrgProfile(null);
        setProvisioningError(false);
        return;
      }

      // Neither user_profile nor client_portal_users found
      if (retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAYS[retryCount] ?? 2000;
        console.warn(
          `[AuthContext] ⚠ No profile found for userId=${userId} — retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES}, DB trigger race condition)`
        );
        profileFetchedForRef.current = null; // allow retry
        await new Promise((resolve) => setTimeout(resolve, delay));
        await fetchProfiles(userId, retryCount + 1);
      } else {
        // All retries exhausted — provisioning is genuinely broken
        console.error(
          `[AuthContext] ✖ PROVISIONING ERROR: No user_profile or client_portal_users record found for userId=${userId} after ${MAX_RETRIES + 1} attempts. ` +
          `Missing records: user_profile (and possibly organization). Dashboard will show recovery state.`
        );
        setProvisioningError(true);
        profileFetchedForRef.current = userId; // stop further retries
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error(`[AuthContext] fetchProfiles threw for userId=${userId} attempt=${retryCount + 1}:`, msg);
      profileFetchedForRef.current = null;
      if (retryCount >= MAX_RETRIES) {
        // Don't leave loading forever on error
        setProvisioningError(true);
      } else {
        const delay = RETRY_DELAYS[retryCount] ?? 2000;
        await new Promise((resolve) => setTimeout(resolve, delay));
        await fetchProfiles(userId, retryCount + 1);
      }
    }
  };

  const refreshProfile = async () => {
    if (user) {
      profileFetchedForRef.current = null;
      setProvisioningError(false);
      await fetchProfiles(user.id);
    }
  };

  useEffect(() => {
    // Guard against React Strict Mode double-invocation mounting two listeners
    if (listenerMountedRef.current) return;
    listenerMountedRef.current = true;

    let mounted = true;

    const onRefreshFailed = () => {
      if (!mounted) return;
      handleRefreshFailure();
    };
    window.addEventListener('sb:refresh-failed', onRefreshFailed);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_OUT') {
        clearAuthState();
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        if (!session) {
          console.warn('[AuthContext] TOKEN_REFRESHED with no session — handling refresh failure');
          if (mounted) handleRefreshFailure();
        }
        return;
      }

      if (event === 'INITIAL_SESSION') {
        if (!session) {
          if (mounted) setLoading(false);
          return;
        }
        setSession(session);
        setUser(session.user);
        try {
          await fetchProfiles(session.user.id);
        } catch (err) {
          console.error('[AuthContext] fetchProfiles threw during INITIAL_SESSION:', err);
          setProvisioningError(true);
        } finally {
          if (mounted) setLoading(false);
        }
        return;
      }

      if (event === 'SIGNED_IN' && session?.user) {
        console.log('[AuthContext] SIGNED_IN →', session.user.id);
        refreshFailureHandledRef.current = false;
        setSessionExpired(false);
        setProvisioningError(false);
        setSession(session);
        setUser(session.user);
        fetchProfiles(session.user.id)
          .catch((err) => {
            console.error('[AuthContext] fetchProfiles threw during SIGNED_IN:', err);
            setProvisioningError(true);
          })
          .finally(() => {
            if (mounted) setLoading(false);
          });
        return;
      }

      if (session?.user) {
        setSession(session);
        setUser(session.user);
        fetchProfiles(session.user.id)
          .catch((err) => {
            console.error('[AuthContext] fetchProfiles threw during auth state change:', err);
            setProvisioningError(true);
          })
          .finally(() => {
            if (mounted) setLoading(false);
          });
      } else {
        clearAuthState();
      }
    });

    return () => {
      mounted = false;
      listenerMountedRef.current = false;
      window.removeEventListener('sb:refresh-failed', onRefreshFailed);
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signUp = async (email: string, password: string, metadata: Record<string, string> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    if (data.session) {
      setSession(data.session);
      setUser(data.session.user);
      fetchProfiles(data.session.user.id);
    }
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.session) {
      setSession(data.session);
      setUser(data.session.user);
      profileFetchedForRef.current = null;
      setProvisioningError(false);
      fetchProfiles(data.session.user.id).finally(() => setLoading(false));
    }
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    clearAuthState();
    router.replace('/login');
  };

  const isAdmin = () => userProfile?.role === 'admin' || userProfile?.role === 'org_owner';
  const isOwner = () => userProfile?.role === 'org_owner';
  const canManageUsers = () => isAdmin();
  const canCreatePOs = () => userProfile?.role !== 'operator';
  const isDemoOrg = () => orgProfile?.isDemo === true;

  const isTrialExpired = () => {
    if (!orgProfile?.trialEndsAt) return false;
    if (orgProfile.subscriptionStatus === 'active') return false;
    return new Date(orgProfile.trialEndsAt) < new Date();
  };

  const isReadOnly = () => {
    if (clientPortalUser) return true;
    return isTrialExpired();
  };

  const trialDaysRemaining = () => {
    if (!orgProfile?.trialEndsAt) return 0;
    const diff = new Date(orgProfile.trialEndsAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const isClientPortalUserFlag = !!clientPortalUser;

  return (
    <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:470:4" data-component-path="src/contexts/AuthContext.tsx" data-component-line="470" data-component-end-line="497" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%7D"
      value={{
        user,
        session,
        userProfile,
        orgProfile,
        clientPortalUser,
        isClientPortalUser: isClientPortalUserFlag,
        loading,
        sessionExpired,
        provisioningError,
        clearSessionExpired,
        signUp,
        signIn,
        signOut,
        refreshProfile,
        isAdmin,
        isOwner,
        canManageUsers,
        canCreatePOs,
        isTrialExpired,
        isReadOnly,
        trialDaysRemaining,
        isDemoOrg,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
